import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "../lib/api";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const navigate = useNavigate();
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/notifications`);
      setData(data);
    } catch (err) {
      console.warn("[notifications] poll failed:", err?.response?.status || err?.message);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 25000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const markAll = async () => {
    await api.post(`/notifications/mark-all-read`).catch(() => {});
    await load();
  };

  const click = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    if (n.ref_url) navigate(n.ref_url);
    await load();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="csc-btn-ghost !p-2 !px-2 relative"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell size={16} />
        {data.unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#39ff14] text-[#050d1a] text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-unread-badge"
          >
            {data.unread > 99 ? "99+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 csc-card overflow-hidden z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(0,170,255,0.18)]">
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8]">Notifications</div>
            <button onClick={markAll} className="text-[10px] font-mono-tech uppercase tracking-wider text-[#00aaff] hover:text-[#39ff14] flex items-center gap-1" data-testid="mark-all-read">
              <CheckCheck size={12} /> Mark all read
            </button>
          </div>
          <div className="max-h-96 overflow-auto">
            {data.items.length === 0 ? (
              <div className="px-4 py-8 text-center text-[#94a3b8] text-xs font-mono-tech uppercase tracking-wider">
                You&apos;re all caught up
              </div>
            ) : (
              data.items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => click(n)}
                  className={`w-full text-left px-4 py-3 border-b border-[rgba(0,170,255,0.18)] last:border-none hover:bg-[#00aaff]/5 ${!n.read ? "bg-[#00aaff]/5" : ""}`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#39ff14] mt-2" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{n.title}</div>
                      {n.body && <div className="text-xs text-[#94a3b8] truncate mt-0.5">{n.body}</div>}
                      <div className="font-mono-tech text-[10px] text-[#94a3b8] mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
          <div className="px-4 py-2 border-t border-[rgba(0,170,255,0.18)]">
            <Link to="/settings/notifications" onClick={() => setOpen(false)} className="block text-center text-xs font-mono-tech uppercase tracking-wider text-[#94a3b8] hover:text-[#00aaff]" data-testid="open-notification-settings">
              Notification settings →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
